import React, { useContext, useMemo, useRef, useState } from 'react';
import { Button, Modal, AsyncDataHandler, Toast } from '@ds';
import { ButtonSize } from '@ds/elements/Button/Button.types';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { IChecklistItem } from 'src/shared/types/Documents';
import PassportItem from './PassportItem';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { PassportInfo } from 'src/shared/types/Case';
import { useGetBeneficiaryPassports  } from 'src/shared/queries/useGetBeneficiaryPassports';
import { useGetRemoteBeneficiaryId } from 'src/shared/queries/useGetRemoteBeneficiaryId';
import styles from './PassportDocumentCenterModal.module.scss';
import { useGetDependents } from 'src/shared/queries/useGetDependents';

interface PassportDocumentCenterModalProps {
  checklistItem?: IChecklistItem;
  onClose: () => void;
  onUpdate?: (selectedPassportIds: number[], passportInfoDocs: PassportInfo[] | undefined) => void;
  isVisible: boolean;
  memberName?: string;
  allCasePassports?: boolean;
  allCasePassportIds?: number[];
}

const TITLES = {
  passports: 'Passports',
  existingPassports: 'Existing Passports',
  addPassports: 'Add Passports',
};

const PassportDocumentCenterModal: React.FC<PassportDocumentCenterModalProps> = ({
  checklistItem,
  onClose,
  onUpdate,
  isVisible = false,
  allCasePassports,
  allCasePassportIds,
}) => {

  const toastRef = useRef<IToastr>(null);
  const { beneId } = useContext(BeneDocAdditionalContext);
  const [selectedPassportIds, setSelectedPassportIds] = useState<number[]>([]);
  const { data: remoteBeneId, isLoading: isRemoteBeneIdLoading } = useGetRemoteBeneficiaryId(beneId ?? 0);
  const { data: dependents } = useGetDependents(beneId ?? 0);
  //The endpoint to get passports, for WHATEVER reason, was built to return all dependent ids as well as long as both beneId and dependentId are provided. So we only need 1 dependentId to get all passports
  const dependentId = allCasePassports ? dependents?.[0].dependentId : checklistItem?.dependent?.dependentId;
  const passportBeneId = checklistItem?.dependent?.dependentId ? undefined : beneId ?? 0;
  const { data: passportInfoDocs, isLoading: isPassportInfoDocsLoading } = useGetBeneficiaryPassports(passportBeneId, dependentId);
  const isLoading = isRemoteBeneIdLoading || isPassportInfoDocsLoading;
  // Determine ownerId from checklistItem beneficiary or dependent
  const ownerId = checklistItem?.beneficiary?.beneId ?? checklistItem?.dependent?.dependentId;

  const filteredPassportInfoDocs = useMemo(() => {
    if (!passportInfoDocs) return [];

    return passportInfoDocs.filter((passportInfo) => {
     
      if (passportInfo.dependentId != null) {
        // For dependent passports: include local dependent and, if feature flag is enabled, any remote dependents
        return true
      } else {
        // For beneficiary passports: include local bene and remote bene (if feature flag is enabled)
        const ids = [ownerId, remoteBeneId];
        return ids.includes(passportInfo.beneId);
      }
    });
  }, [passportInfoDocs, ownerId, remoteBeneId]);

  const submitPassport = async () => {
    if (onUpdate) {
      onUpdate(selectedPassportIds, passportInfoDocs);
    }

    if (onClose) {
      setSelectedPassportIds([]);
      onClose();
    }
  };

  const handleItemToggle = (passportId: number, isSelected: boolean) => {
    let isAlreadyAssigned = false;
    if (allCasePassports) {
      // Check if passportId is already in allCasePassportIds
      isAlreadyAssigned = allCasePassportIds?.some((casePassportId) => casePassportId === passportId) ?? false;
    } else {
      // Check if passportId is already in any checklistItemGroup
      isAlreadyAssigned =
        checklistItem?.checklistItemGroups?.some((group) => group.passportInfoId === passportId) ?? false;
    }
    if (isSelected && isAlreadyAssigned) {
      // Show error using toastRef
      if (toastRef?.current) {
        toastRef.current.show({
          severity: 'error',
          message: 'This passport is already linked to this case',
        });
      }
      return;
    }

    setSelectedPassportIds((prevSelected) =>
      isSelected ? [...prevSelected, passportId] : prevSelected.filter((id) => id !== passportId)
    );
  };

  return (
    <Modal
      visible={isVisible}
      className={styles['passport-document-center-modal-wrapper']}
      modalClassName={styles['passport-document-center-modal']}
      onModalClosed={onClose}
      disableClickOutside
      title=""
    >
      <AsyncDataHandler isLoading={isLoading} loader={{ width: '100%' }}>
        <div className={`${styles['passport-document-center-header']} flex justify-between items-center mx-4 my-8`}>
          <p>{TITLES.passports}</p>

          <Button
            className={styles['submit-button']}
            onClick={submitPassport}
            size={ButtonSize.large}
            disabled={!selectedPassportIds || selectedPassportIds.length === 0}
          >
            {TITLES.addPassports}
          </Button>
        </div>
        <div className={`${styles['passport-document-center-body']} my-8`}>
          <p className="mx-4 mb-3">{TITLES.existingPassports}</p>

          <div className="flex flex-wrap gap-2 sm:mx-4">
            {allCasePassports ? (
              passportInfoDocs && passportInfoDocs.length > 0 ? (
                passportInfoDocs?.map((passportInfo) => (
                  <PassportItem
                    key={passportInfo.passportId}
                    passportInfo={passportInfo}
                    allCasePassports={allCasePassports}
                    onItemToggle={(isSelected) => handleItemToggle(passportInfo.passportId, isSelected)}
                    isSelected={selectedPassportIds.includes(passportInfo.passportId)}
                  />
                ))
              ) : (
                <span>No passports found</span>
              )
            ) : !filteredPassportInfoDocs || filteredPassportInfoDocs.length === 0 ? (
              <span>No passports found</span>
            ) : (
              filteredPassportInfoDocs.map((passportInfo) => (
                <PassportItem
                  key={passportInfo.passportId}
                  passportInfo={passportInfo}
                  allCasePassports={allCasePassports}
                  onItemToggle={(isSelected) => handleItemToggle(passportInfo.passportId, isSelected)}
                  isSelected={selectedPassportIds.includes(passportInfo.passportId)}
                />
              ))
            )}
          </div>
        </div>
        <Toast ref={toastRef} />
      </AsyncDataHandler>
    </Modal>
  );
};

export default PassportDocumentCenterModal;
